import React, { useCallback, useState } from "react";
import Button from "@amzn/awsui-components-react/polaris/button";
import {
  openFeedbackModal,
  waitForLoomNavbarLoaded,
} from "@amzn/loom-navbar-api";

import { isV2Enabled, setV2Enabled } from "./featureFlag";
import { trackButtonClick } from "../telemetry/telemetry";

/**
 * Compact "Visit X view" affordance that flips the V2 experience flag
 * and reloads. Mounted in both trees:
 *
 * - Legacy `<Content/>` renders "Visit new view" (flip on).
 * - V2 `<AppShell/>` renders "Visit old view" (flip off).
 *
 * Rendered inline; callers own placement. V2 portals it into
 * Cloudscape's drawer-triggers row (shares toolbar stickiness);
 * Legacy wraps it in a `position: fixed` overlay.
 *
 * `App` reads `isV2Enabled()` synchronously at mount, so a soft
 * in-place switch would require lifting the flag into React state
 * and remounting both trees. A full reload keeps this short-lived
 * opt-in surface simple to reason about.
 *
 * On the V2 → legacy path only, we open the Loom Feedback modal
 * before flipping. The routing configs (form schema, SIM channel,
 * cooldowns, ticket-reference visibility) are managed server-side
 * under Admin → Feedback Routing; the client just picks the right
 * `formKey` for the page and bypasses prompt control with
 * `forceShow: true` since the flip is user-initiated. The flip and
 * reload are deferred until the modal is submitted, cancelled, or
 * errors so the reload doesn't tear the modal down mid-render.
 *
 * Retire alongside `featureFlag.ts` once V2 is fully rolled out.
 */

const FEEDBACK_SERVICE_KEY = "devices-asin-central";
const FEEDBACK_FORM_KEY_VALIDATION = "validation-dashboard-switch";
const FEEDBACK_FORM_KEY_GENERAL = "experience-switch-general";

function pickSwitchFeedbackFormKey(pathname: string): string {
  return pathname.startsWith("/validation")
    ? FEEDBACK_FORM_KEY_VALIDATION
    : FEEDBACK_FORM_KEY_GENERAL;
}

export const V2Toggle: React.FC = () => {
  // Seed from storage once. The component reloads the page on change,
  // so re-syncing across tabs is not needed within this instance.
  const [enabled, setEnabled] = useState<boolean>(() => isV2Enabled());

  const handleClick = useCallback(async () => {
    const next = !enabled;

    // Apply the flip + telemetry flush + reload. Runs directly on the
    // legacy → V2 path, and via the feedback modal's lifecycle
    // callbacks on the V2 → legacy path. Guarded so submit/cancel
    // races (or a cancel-after-submit sequence) reload exactly once.
    let finalized = false;
    const finalize = async () => {
      if (finalized) return;
      finalized = true;
      setEnabled(next);
      setV2Enabled(next);
      // Await the tracking call so the Katal log flushes before we
      // reload — otherwise the in-flight XHR is cancelled by the
      // navigation and the event is lost.
      try {
        await trackButtonClick("appShell", "v2Preference", {
          from: enabled ? "v2" : "legacy",
          to: next ? "v2" : "legacy",
        });
      } catch {
        // Telemetry failures never block the user-visible flip.
      }
      // Full reload so `App`'s synchronous flag read picks up the new
      // value and mounts the matching tree from scratch.
      if (typeof window !== "undefined") {
        window.location.reload();
      }
    };

    // Only prompt on V2 → legacy. Going legacy → V2 stays a silent
    // opt-in.
    if (!enabled) {
      await finalize();
      return;
    }

    // Fields, prompt control, SIM channel, and ticket-reference
    // visibility live on the routing config; we only pass what varies
    // per call site.
    try {
      await waitForLoomNavbarLoaded();
      const result = await openFeedbackModal({
        formKey: pickSwitchFeedbackFormKey(window.location.pathname),
        serviceKey: FEEDBACK_SERVICE_KEY,
        title: "Help us improve the new experience",
        forceShow: true,
        metadata: {
          source: "switch-to-old-button",
          page: window.location.pathname,
        },
        onSubmit: () => {
          void finalize();
        },
        onCancel: () => {
          void finalize();
        },
        onError: () => {
          void finalize();
        },
      });
      // `forceShow: true` should always produce `prompted: true`, but
      // fail open (auth disabled, no matching routing config, etc.)
      // so the button never leaves the user stranded on V2.
      if (!result?.prompted) {
        await finalize();
      }
    } catch {
      // Navbar not loaded or modal API unavailable — never block the
      // user-visible flip on the feedback path.
      await finalize();
    }
  }, [enabled]);

  // Label describes where clicking takes the user, so it flips with
  // the current side.
  const label = enabled ? "Visit old view" : "Visit new view";

  return (
    <div data-testid="v2-toggle">
      <Button variant="normal" onClick={handleClick} ariaLabel={label}>
        {label}
      </Button>
    </div>
  );
};

export default V2Toggle;
